import React, { useState, useEffect, useCallback } from "react";
import {
    ChevronLeft,
    Camera,
    X,
    FacebookIcon,
    DownloadIcon,
    TwitterIcon,
    AlertCircle,
} from "lucide-react";
import { useAtom } from "jotai";
import { activeChildDetail, appConstants, PhotoGalleryAtom } from "../../atomstore/store";
import { useRequestProcessor } from "../../requestProcessor";
import { fetchPhotos } from "../../queryfunctions/queryfunctions";
import SpinnerLoading from "../common/SpinnerLoading";
import { useNavigate } from "react-router-dom";

import { Capacitor } from "@capacitor/core";
import { Media } from "@capacitor-community/media";
import { Toast } from "@capacitor/toast";

const FACEBOOK_APPID = "1663256590659601";

// ✅ Helper: ensure album exists on mobile
const ensureDemoAlbum = async () => {
    let { albums } = await Media.getAlbums();
    const albumName = "Footprints Album";
    let demoAlbum = undefined;

    if (Capacitor.getPlatform() === "android") {
        const albumsPath = (await Media.getAlbumsPath()).path;
        demoAlbum = albums.find(
            (a) => a.name === albumName && a.identifier.startsWith(albumsPath)
        );
    } else {
        demoAlbum = albums.find((a) => a.name === albumName);
    }

    if (!demoAlbum) {
        await Media.createAlbum({ name: albumName });
        albums = await Media.getAlbums();
        demoAlbum = albums.albums?.find((a) => a.name === albumName);
    }

    const message = demoAlbum
        ? "Image downloaded successfully"
        : "Image download failed";
    Toast.show({ text: message, duration: "short" });

    return demoAlbum ? demoAlbum.identifier : "";
};

// ✅ Helper: save photo to mobile album
const DownloadImageMobile = async (fileName, base64Data) => {
    const image = await Media.savePhoto({
        path: base64Data,
        albumIdentifier: await ensureDemoAlbum(),
        fileName,
    });
    return image;
};

// ✅ Main Component
const Gallery = () => {
    const [constants] = useAtom(appConstants);
    const navigate = useNavigate();
    const [photos, setPhotos] = useAtom(PhotoGalleryAtom);
    const [activeChild] = useAtom(activeChildDetail);
    const { useQueryWrapper } = useRequestProcessor();
    const [selectedIndex, setSelectedIndex] = useState(null); // 🔹 Track index instead of only photo

    // ✅ Load photos
    const { isLoading } = useQueryWrapper(
        ["photos", activeChild?.id],
        () => fetchPhotos(0, photos),
        {
            enabled: !!activeChild?.id,
            onSuccess: (data) => setPhotos(data || []),
            refetchOnWindowFocus: false,
        }
    );

    // ✅ Universal Download Logic
    const downloadImage = async (imageUrl, e) => {
        if (e && e.preventDefault) e.preventDefault();
        if (!imageUrl) return;

        try {
            const platform = Capacitor.getPlatform();
            const disableCache = true;
            const fetchUrl = `${imageUrl}${disableCache ? `?dummy=${Math.floor(Date.now())}` : ""
                }`;

            if (platform === "android" || platform === "ios") {
                const response = await fetch(fetchUrl);
                const blob = await response.blob();

                const convertBlobToBase64 = (blob) =>
                    new Promise((resolve, reject) => {
                        const reader = new FileReader();
                        reader.onerror = reject;
                        reader.onload = () => resolve(reader.result);
                        reader.readAsDataURL(blob);
                    });

                const base64Data = await convertBlobToBase64(blob);
                const fileName = `image_${Date.now()}.png`;

                await DownloadImageMobile(fileName, base64Data);
            } else {
                fetch(fetchUrl, { method: "GET" })
                    .then((response) => response.arrayBuffer())
                    .then((buffer) => {
                        const url = window.URL.createObjectURL(new Blob([buffer]));
                        const link = document.createElement("a");
                        link.href = url;
                        link.setAttribute(
                            "download",
                            imageUrl.substr(imageUrl.lastIndexOf("/") + 1)
                        );
                        document.body.appendChild(link);
                        link.click();
                        document.body.removeChild(link);
                    })
                    .catch((err) => {
                        console.error("Web download error:", err);
                        Toast.show({ text: "Failed to download image", duration: "short" });
                    });
            }
        } catch (error) {
            console.error("Error downloading image:", error);
            Toast.show({ text: "Error downloading image", duration: "short" });
        }
    };

    // 🌐 Social sharing
    const shareFacebook = (url) => {
        const caption = `${activeChild?.name || "Child"} in ${activeChild?.["room.name"] || "Room"
            } at ${activeChild?.["center.name"] || "Center"}`;
        const shareUrl = `https://www.facebook.com/dialog/feed?display=popup&app_id=${FACEBOOK_APPID}&link=${url}&title=${caption}`;
        window.open(shareUrl);
    };

    const shareTwitter = (url) => {
        const caption = `${activeChild?.name || "Child"} in ${activeChild?.["room.name"] || "Room"
            } at ${activeChild?.["center.name"] || "Center"}`;
        const tweetUrl = `https://twitter.com/intent/tweet?text=${caption}&url=${url}`;
        window.open(tweetUrl);
    };

    // ✅ Handle swipe gestures
    const handleTouchStart = useCallback((e) => {
        e.currentTarget.dataset.startX = e.touches[0].clientX;
    }, []);

    const handleTouchEnd = useCallback(
        (e) => {
            const startX = parseFloat(e.currentTarget.dataset.startX || "0");
            const endX = e.changedTouches[0].clientX;
            const diff = startX - endX;

            if (Math.abs(diff) > 50) {
                if (diff > 0 && selectedIndex < photos.length - 1)
                    setSelectedIndex(selectedIndex + 1); // Swipe left → next
                else if (diff < 0 && selectedIndex > 0)
                    setSelectedIndex(selectedIndex - 1); // Swipe right → prev
            }
        },
        [selectedIndex, photos?.length]
    );

    // ✅ Handle keyboard arrows (desktop)
    useEffect(() => {
        const handleKey = (e) => {
            if (selectedIndex !== null) {
                if (e.key === "ArrowRight" && selectedIndex < photos?.length - 1)
                    setSelectedIndex((prev) => prev + 1);
                else if (e.key === "ArrowLeft" && selectedIndex > 0)
                    setSelectedIndex((prev) => prev - 1);
                else if (e.key === "Escape") setSelectedIndex(null);
            }
        };
        window.addEventListener("keydown", handleKey);
        return () => window.removeEventListener("keydown", handleKey);
    }, [selectedIndex, photos?.length]);

    if (isLoading) return <SpinnerLoading />;

    return (
        <div className="fixed inset-0 bg-gray-50 z-50 overflow-y-auto">
            {/* Header */}
            <div className="sticky top-0 bg-white border-b p-4 flex items-center space-x-3">
                <button onClick={() => navigate(-1)}>
                    <ChevronLeft className="w-6 h-6 text-gray-600" />
                </button>
                <h2 className="text-lg font-bold text-gray-800 cursor-pointer" onClick={() => navigate(-1)}>Photo Gallery</h2>
            </div>
            <div class="m-2 p-2 py-3 text-sm rounded-lg bg-red-400 text-white flex items-center" role="alert"> <AlertCircle /> &nbsp;  {constants?.ArchiveMessage}</div>

            {/* Grid */}
            <div className="p-4">
                {photos?.length > 0 ? (
                    <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 lg:grid-cols-6 xl:grid-cols-8 gap-2">
                        {photos.map((photo, idx) => (
                            <div
                                key={idx}
                                className="aspect-square rounded-xl overflow-hidden bg-gray-100"
                                onClick={() => setSelectedIndex(idx)}
                            >
                                <img
                                    src={photo.src}
                                    alt={` ${idx}`}
                                    className="w-full h-full object-cover cursor-pointer hover:scale-105 transition-transform"
                                />
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="flex flex-col items-center justify-center py-20 text-gray-400">
                        <Camera className="w-10 h-10 mb-2 text-cyan-400" />
                        <p>No photos found</p>
                    </div>
                )}
            </div>

            {/* Overlay Modal */}
            {selectedIndex !== null && (
                <div
                    className="fixed inset-0 bg-black bg-opacity-90 flex flex-col justify-center items-center z-[60] p-4 overflow-y-auto"
                    onTouchStart={handleTouchStart}
                    onTouchEnd={handleTouchEnd}
                >
                    <button
                        className="absolute top-5 right-5 text-white text-2xl z-[70]"
                        onClick={() => setSelectedIndex(null)}
                    >
                        <X className="w-8 h-8" />
                    </button>

                    <div className="flex-1 flex justify-center items-center relative min-h-0 overflow-hidden">
                        {/* Left/Right overlay buttons for desktop */}
                        {/* {selectedIndex > 0 && (
                            <button
                                className="absolute left-4 text-white opacity-70 hover:opacity-100"
                                onClick={() => setSelectedIndex(selectedIndex - 1)}
                            >
                                <ChevronLeft className="w-10 h-10" />
                            </button>
                        )}
                        {selectedIndex < photos.length - 1 && (
                            <button
                                className="absolute right-4 text-white opacity-70 hover:opacity-100"
                                onClick={() => setSelectedIndex(selectedIndex + 1)}
                            >
                                <ChevronLeft className="w-10 h-10 rotate-180" />
                            </button>
                        )} */}

                        <img
                            src={photos[selectedIndex]?.src}
                            alt="Enlarged"
                            className="max-h-[70vh] max-w-full w-auto h-auto object-contain rounded-lg"
                        />
                    </div>
                    <div className="flex gap-6 mt-6 mb-4">
                        <button
                            className="bg-gray-700 rounded-full p-3 shadow-lg"
                            onClick={(e) => downloadImage(photos[selectedIndex]?.src, e)}
                        >
                            <DownloadIcon className="text-white text-xl" />
                        </button>

                        <button
                            className="bg-[#4267B2] rounded-full p-3 shadow-lg"
                            onClick={() => shareFacebook(photos[selectedIndex]?.src)}
                        >
                            <FacebookIcon className="text-white text-xl" />
                        </button>

                        <button
                            className="bg-[#1DA1F2] rounded-full p-3 shadow-lg"
                            onClick={() => shareTwitter(photos[selectedIndex]?.src)}
                        >
                            <TwitterIcon className="text-white text-xl" />
                        </button>
                    </div>
                    <div className="mt-5 mb-6 flex justify-center items-center gap-10">
                        {/* Left (Previous) */}
                        <button
                            onClick={() => {
                                if (selectedIndex > 0)
                                    setSelectedIndex(selectedIndex - 1);
                                else setSelectedIndex(photos.length - 1); // optional: loop back to end
                            }}
                            className="bg-gray-900 rounded-full w-12 h-12 flex justify-center items-center shadow-lg border border-gray-200 hover:scale-105 transition-transform"
                        >
                            <ChevronLeft className="w-7 h-7 text-white" />
                        </button>

                        {/* Right (Next) */}
                        <button
                            onClick={() => {
                                if (selectedIndex < photos.length - 1)
                                    setSelectedIndex(selectedIndex + 1);
                                else setSelectedIndex(0); // optional: loop to start
                            }}
                            className="bg-gray-900 rounded-full w-12 h-12 flex justify-center items-center shadow-lg border border-gray-200 hover:scale-105 transition-transform"
                        >
                            <ChevronLeft className="w-7 h-7 rotate-180 text-white" />
                        </button>
                    </div>


                </div>
            )}
        </div>
    );
};

export default Gallery;
